import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { profile, getBodyMap } from "../store.js";
import { icon } from "../lib/icons.js";
import { backLink } from "../components/common.js";
import { spinner, errorState, emptyState, toast, openModal, confirmDialog } from "../components/ui.js";
import { lineChart } from "../components/Chart.js";
import { toDisplayLength, toStorageCm, lengthUnit, fmtDate, parseDate } from "../lib/format.js";
import { proportions } from "../lib/proportions.js";
import { navyBodyFat } from "../lib/health.js";
import { navigate } from "../core/router.js";

// Canonical site metadata and anatomical landmark guidance.
// Site keys match the server whitelist (routes/measurements.ts).
const SITES = [
  ["waist", "Waist"], ["hips", "Hips"], ["neck", "Neck"],
  ["shoulders", "Shoulders"], ["chest", "Chest"], ["arm", "Arm"],
  ["forearm", "Forearm"], ["thigh", "Thigh"], ["calf", "Calf"],
];

const SITE_META = {
  waist: {
    label: "Waist",
    category: "core",
    icon: "target",
    tag: "Core Health & Navy BF%",
    menLandmark: "Horizontally at navel (belly button) level. Stand tall, relax your abdomen, and measure at the end of a normal exhale. Do not suck in.",
    womenLandmark: "Horizontally at the natural waist (narrowest torso point, usually ~1 inch above navel). Relax your abdomen and measure at the end of a normal exhale.",
    rationale: "Men store abdominal fat around the umbilicus (navel is an invariant anatomical landmark). Women store gynoid fat lower, so the natural waist is the narrowest point for WHR and the Navy Body Fat formula.",
  },
  hips: {
    label: "Hips",
    category: "core",
    icon: "heart",
    tag: "Powers WHR & Navy BF%",
    landmark: "Widest horizontal circumference over the buttocks / gluteal peak with heels together. Ensure the tape stays parallel to the floor.",
    rationale: "Essential companion to waist for the Waist-to-Hip Ratio (WHO health risk) and female Navy Body Fat calculation.",
  },
  neck: {
    label: "Neck",
    category: "core",
    icon: "shield",
    tag: "Powers Navy BF%",
    landmark: "Horizontally just below the larynx (Adam's apple), tape sloping slightly downward in front. Keep neck muscles relaxed.",
    rationale: "Used in the U.S. Navy formula to normalize body volume against skeletal frame width.",
  },
  chest: {
    label: "Chest",
    category: "upper",
    icon: "flame",
    tag: "Upper Torso Hypertrophy",
    landmark: "Widest horizontal circumference across the chest/bust (nipple line for men, fullest point for women), arms relaxed at sides, mid-breath.",
    rationale: "Tracks pectoral, latissimus, and upper-back muscular development.",
  },
  shoulders: {
    label: "Shoulders",
    category: "upper",
    icon: "dumbbell",
    tag: "Powers V-Taper Ratio",
    landmark: "Widest horizontal circumference around the outside of the deltoids (shoulder caps) with arms resting naturally at sides.",
    rationale: "Tracks deltoid development and provides the upper anchor for the shoulder-to-waist V-taper ratio.",
  },
  arm: {
    label: "Arm",
    category: "upper",
    icon: "pulse",
    tag: "Bicep / Tricep Peak",
    landmark: "Thickest point of the upper arm (bicep peak). Choose relaxed (arm hanging) or flexed at 90° and keep it consistent across logs.",
    rationale: "Primary metric for arm hypertrophy. Staying consistent on flexed vs unflexed is key.",
  },
  forearm: {
    label: "Forearm",
    category: "upper",
    icon: "grip",
    tag: "Grip & Forearm",
    landmark: "Widest circumference of the forearm below the elbow, with fist clenched.",
    rationale: "Tracks forearm hypertrophy and grip musculature.",
  },
  thigh: {
    label: "Thigh",
    category: "lower",
    icon: "trophy",
    tag: "Quad & Hamstring",
    landmark: "Widest point of the upper thigh, just below the gluteal fold, standing with bodyweight distributed evenly on both legs.",
    rationale: "Tracks quadriceps and hamstring muscle growth.",
  },
  calf: {
    label: "Calf",
    category: "lower",
    icon: "play",
    tag: "Gastrocnemius",
    landmark: "Widest circumference of the calf muscle (gastrocnemius), standing with bodyweight distributed evenly.",
    rationale: "Tracks lower leg muscular development.",
  },
};

const CATEGORIES = [
  { key: "core", title: "Core & Health", hint: "Powers Body Fat % & Health Ratios", sites: ["waist", "hips", "neck"] },
  { key: "upper", title: "Upper Body", hint: "Muscle Growth & V-Taper", sites: ["chest", "shoulders", "arm", "forearm"] },
  { key: "lower", title: "Lower Body", hint: "Leg Hypertrophy", sites: ["thigh", "calf"] },
];

const siteLabel = (s) => SITE_META[s]?.label || s;

// Silhouette markers for front-{sex}.svg (viewBox 724×1448 -> 1:2 box)
const MARKERS = [
  { site: "neck", x: 50, y: 20, side: "r" },
  { site: "shoulders", x: 50, y: 24.5, side: "l" },
  { site: "chest", x: 50, y: 29, side: "r" },
  { site: "arm", x: 27, y: 32, side: "l" },
  { site: "waist", x: 50, y: 42, side: "r" },
  { site: "forearm", x: 21, y: 45.5, side: "l" },
  { site: "hips", x: 50, y: 49, side: "r" },
  { site: "thigh", x: 40, y: 55, side: "l" },
  { site: "calf", x: 39, y: 78, side: "l" },
];

const svgCache = {};
function loadSilhouette(sex) {
  const url = `/svgs/front-${sex}.svg`;
  if (!svgCache[url]) svgCache[url] = fetch(url).then((r) => r.text()).catch(() => "");
  return svgCache[url];
}

export default function Measurements() {
  const root = h("div", { class: "screen" });
  let data = null;
  let activeView = "cards";
  try {
    const saved = localStorage.getItem("wt_ms_view");
    if (saved === "cards" || saved === "map") activeView = saved;
  } catch {}
  let guideOpen = false;
  const expandedCharts = new Set();

  load();

  async function load() {
    setChildren(root, backLink("Progress", "/history"), spinner());
    try {
      data = await api.measurements();
      // If user has 0 measurements, open guide by default to orient them
      if (!data.sites?.length) guideOpen = true;
      render();
    } catch (e) {
      setChildren(root, backLink("Progress", "/history"), errorState(e.message, load));
    }
  }

  function currentMap() {
    const m = {};
    for (const s of data.sites || []) m[s.site] = s.current_cm;
    return m;
  }

  function setView(v) {
    activeView = v;
    try { localStorage.setItem("wt_ms_view", v); } catch {}
    render();
  }

  function toggleGuide() {
    guideOpen = !guideOpen;
    render();
  }

  function toggleChart(site) {
    if (expandedCharts.has(site)) expandedCharts.delete(site);
    else expandedCharts.add(site);
    render();
  }

  function render() {
    setChildren(root,
      backLink("Progress", "/history"),
      headerSection(),
      guideSection(),
      viewToggle(),
      proportionsCard(),
      activeView === "cards" ? categorizedCardsView() : silhouetteCard()
    );
  }

  // ── Header & Action Bar ─────────────────────────────────────────────────────
  function headerSection() {
    return h("div", null,
      h("div", { class: "ms-header-row" },
        h("div", null,
          h("h1", { style: { margin: "0" } }, "Measurements"),
          h("p", { class: "dim", style: { margin: "2px 0 0", fontSize: ".84rem" } },
            "Track body composition, health ratios, and muscle growth.")
        ),
        h("div", { class: "ms-header-actions" },
          h("button", {
            class: "btn btn-ghost btn-sm",
            style: { display: "flex", alignItems: "center", gap: "5px" },
            onClick: toggleGuide,
          }, icon("info", { size: 16 }), guideOpen ? "Hide guide" : "Guide"),
          h("button", {
            class: "btn btn-primary btn-sm",
            style: { display: "flex", alignItems: "center", gap: "5px" },
            onClick: () => openLogModal("waist"),
          }, icon("plus", { size: 16 }), "Log measurement")
        )
      )
    );
  }

  // ── Educational Measurement Protocol Guide ──────────────────────────────────
  function guideSection() {
    if (!guideOpen) return null;
    const sex = profile()?.gender || getBodyMap();
    const isFemale = sex === "female";

    return h("div", { class: "card ms-guide-card" },
      h("div", { class: "row-between", style: { alignItems: "center" } },
        h("div", { style: { fontWeight: 700, fontSize: ".95rem", display: "flex", alignItems: "center", gap: "6px" } },
          icon("target", { size: 18 }), "How to Measure Accurately & Consistently"),
        h("button", { class: "icon-btn ghost btn-sm", "aria-label": "Close guide", onClick: toggleGuide }, icon("x", { size: 16 }))
      ),
      h("p", { class: "dim", style: { fontSize: ".8rem", margin: "6px 0 10px", lineHeight: 1.4 } },
        "Circumference measurements are susceptible to water retention, digestion, posture, and breathing. Following the protocol below ensures your numbers reflect real muscle and fat changes:"),

      // 4 Golden Rules
      h("div", { class: "ms-rules-grid" },
        h("div", { class: "ms-rule-item" },
          h("div", { class: "ms-rule-title" }, "☀️ 1. Morning, Fasted"),
          h("div", { class: "ms-rule-desc" }, "Measure right after waking and using the bathroom, before eating or drinking. Food & hydration can swing waist by 1–3 cm during the day.")
        ),
        h("div", { class: "ms-rule-item" },
          h("div", { class: "ms-rule-title" }, "📏 2. Level, Snug Tape"),
          h("div", { class: "ms-rule-desc" }, "Keep tape parallel to the floor (use a mirror). Snug flat against bare skin, never pulled so tight that it pinches or indents soft tissue.")
        ),
        h("div", { class: "ms-rule-item" },
          h("div", { class: "ms-rule-title" }, "💨 3. Normal Exhale"),
          h("div", { class: "ms-rule-desc" }, "Stand tall with relaxed abdomen. Take two calm breaths; read the tape at the end of normal expiration. Never suck in or puff out.")
        ),
        h("div", { class: "ms-rule-item" },
          h("div", { class: "ms-rule-title" }, "📅 4. Every 2–4 Weeks"),
          h("div", { class: "ms-rule-desc" }, "Unlike daily scale weight, circumference shifts gradually. Measuring every 2 to 4 weeks filters out temporary digestion and hydration noise.")
        )
      ),

      // Sex-Specific Waist Protocol Callout
      h("div", { style: { marginTop: "12px", borderTop: "1px solid var(--border)", paddingTop: "10px" } },
        h("div", { style: { fontWeight: 700, fontSize: ".88rem", display: "flex", alignItems: "center", gap: "6px" } },
          "📍 The Waist Difference: Men vs. Women"),
        h("div", { class: "ms-gender-split" },
          h("div", { class: `ms-gender-box male ${!isFemale ? "highlight" : ""}` },
            h("div", { class: "ms-gender-title" }, "👨 Men: Navel Level (Belly Button)"),
            h("div", { class: "ms-rule-desc" },
              "Measure horizontally at the level of the belly button. Men store visceral fat centrally; the navel is an invariant anatomical landmark matching the U.S. Navy and WHO standards.")
          ),
          h("div", { class: `ms-gender-box female ${isFemale ? "highlight" : ""}` },
            h("div", { class: "ms-gender-title" }, "👩 Women: Natural Waist (Narrowest Point)"),
            h("div", { class: "ms-rule-desc" },
              "Measure horizontally at the narrowest point of your torso (~1 inch above navel). Required for Waist-to-Hip Ratio and the female Navy Body Fat formula (Waist + Hips − Neck).")
          )
        )
      )
    );
  }

  // ── View Switcher ───────────────────────────────────────────────────────────
  function viewToggle() {
    return h("div", { class: "ms-toggle-bar" },
      h("button", {
        class: `ms-toggle-btn ${activeView === "cards" ? "active" : ""}`,
        onClick: () => setView("cards"),
      }, icon("list", { size: 16 }), "All Sites (9)"),
      h("button", {
        class: `ms-toggle-btn ${activeView === "map" ? "active" : ""}`,
        onClick: () => setView("map"),
      }, icon("user", { size: 16 }), "Interactive Body Map")
    );
  }

  // ── Proportions & Composition Summary Card ──────────────────────────────────
  function proportionsCard() {
    const sex = profile()?.gender || getBodyMap();
    const heightCm = profile()?.height_cm;
    const cur = currentMap();
    const ratios = proportions(cur, sex, heightCm);

    // Compute Navy Body Fat if required inputs are present
    let bfEst = null;
    if (cur.waist && cur.neck && heightCm) {
      bfEst = navyBodyFat({
        gender: sex,
        heightCm,
        waistCm: cur.waist,
        neckCm: cur.neck,
        hipsCm: cur.hips,
      });
    }

    if (!ratios.length && bfEst == null) {
      return h("div", { class: "card dim", style: { fontSize: ".82rem", marginBottom: "14px" } },
        "Log your waist + hips (or height in Profile) to see your health ratios and body composition.");
    }

    const statusStyle = {
      good: { background: "var(--success)", color: "#fff" },
      ok: { background: "var(--accent-soft)", color: "var(--accent)" },
      watch: { background: "var(--surface-2)", color: "var(--text-2)" },
    };

    return h("div", { class: "card", style: { marginBottom: "14px" } },
      h("div", { class: "row-between", style: { alignItems: "center", marginBottom: "6px" } },
        h("div", { class: "section-title", style: { margin: 0 } }, "Health & Proportions"),
        bfEst != null
          ? h("div", {
              role: "button",
              class: "badge",
              style: { background: "var(--accent-soft)", color: "var(--accent)", cursor: "pointer", fontWeight: 700 },
              onClick: () => navigate("/weight"),
            }, `Navy Body Fat: ${bfEst}% →`)
          : null
      ),
      ...ratios.map((r) => {
        const st = statusStyle[r.status] || statusStyle.watch;
        return h("div", { class: "row-between", style: { padding: "8px 0", alignItems: "center", borderTop: "1px solid var(--border)" } },
          h("div", null,
            h("div", { style: { fontWeight: 600, fontSize: ".9rem" } }, r.name),
            h("div", { class: "dim", style: { fontSize: ".74rem", marginTop: "1px" } }, r.hint)
          ),
          h("div", { style: { textAlign: "right" } },
            h("div", { style: { fontWeight: 800, fontSize: "1.1rem" } }, r.value.toFixed(2)),
            h("span", { class: "badge", style: { background: st.background, color: st.color, fontSize: ".66rem" } }, r.label)
          )
        );
      }),
      h("p", { class: "dim", style: { fontSize: ".72rem", margin: "8px 0 0", lineHeight: 1.35 } },
        "Waist-to-height ratio (<0.50) and waist-to-hip ratio are validated cardiometabolic markers (WHO/NICE).")
    );
  }

  // ── Categorized Cards View (All 9 Sites) ────────────────────────────────────
  function categorizedCardsView() {
    const siteDataMap = {};
    for (const s of data.sites || []) siteDataMap[s.site] = s;

    return h("div", null,
      ...CATEGORIES.map((cat) => {
        return h("div", { class: "ms-cat-section" },
          h("div", { class: "ms-cat-head" },
            h("div", { class: "ms-cat-title" }, cat.title),
            h("div", { class: "ms-cat-hint" }, cat.hint)
          ),
          h("div", { class: "ms-site-grid" },
            ...cat.sites.map((siteKey) => siteCardItem(siteKey, siteDataMap[siteKey]))
          )
        );
      })
    );
  }

  function siteCardItem(siteKey, s) {
    const meta = SITE_META[siteKey] || { label: siteKey, icon: "target" };
    const goal = data.goals?.[siteKey];
    const entries = s?.entries || [];
    const curVal = s?.current_cm;
    const sex = profile()?.gender || getBodyMap();

    // Specific landmark tag for waist vs other sites
    let landmarkTag = meta.tag;
    if (siteKey === "waist") {
      landmarkTag = sex === "female" ? "Natural waist (~1\" above navel)" : "Navel level (belly button)";
    }

    // 30-day change delta
    let deltaText = null;
    let deltaColor = "var(--text-3)";
    if (entries.length >= 2) {
      const past = around(entries, 30);
      const d = Number(toDisplayLength(curVal)) - Number(toDisplayLength(past));
      const rounded = Math.round(d * 10) / 10;
      if (rounded !== 0) {
        deltaText = `${rounded > 0 ? "▲" : "▼"} ${Math.abs(rounded)} ${lengthUnit()} · 30d`;
        deltaColor = rounded > 0 ? "var(--accent)" : "var(--success)";
      }
    }

    const isExpanded = expandedCharts.has(siteKey);

    return h("div", { class: "ms-card" },
      h("div", { class: "ms-card-top" },
        h("div", { class: "ms-card-name" },
          icon(meta.icon, { size: 18 }),
          meta.label
        ),
        h("div", { class: "ms-card-val" },
          curVal != null
            ? h("span", null,
                h("span", { class: "ms-card-num" }, toDisplayLength(curVal)),
                h("span", { class: "ms-card-unit" }, lengthUnit())
              )
            : h("span", { class: "dim", style: { fontSize: ".82rem" } }, "Not logged")
        )
      ),

      // Landmark and Delta
      h("div", { class: "ms-card-mid" },
        h("span", { class: "ms-landmark-tag" }, landmarkTag),
        deltaText ? h("span", { class: "ms-card-delta", style: { color: deltaColor } }, deltaText) : null
      ),

      // Goal / Target row
      goalRow(siteKey, curVal, goal, entries[0]?.value_cm),

      // Action buttons: + Log and History toggle
      h("div", { class: "ms-card-actions" },
        h("button", {
          class: "btn btn-ghost btn-sm",
          style: { flex: "1", justifyContent: "center" },
          onClick: () => openLogModal(siteKey),
        }, icon("plus", { size: 14 }), "Log"),
        entries.length
          ? h("button", {
              class: `btn btn-ghost btn-sm ${isExpanded ? "active" : ""}`,
              onClick: () => toggleChart(siteKey),
              "aria-label": `${isExpanded ? "Hide" : "Show"} history and chart`,
            }, icon(isExpanded ? "chevronDown" : "chart", { size: 14 }), isExpanded ? "Close" : `History (${entries.length})`)
          : null
      ),

      // Expanded chart and entry list
      isExpanded && entries.length
        ? h("div", { class: "ms-card-chart-wrap" },
            entries.length >= 2
              ? lineChart(entries.map((e) => ({ x: e.logged_at, y: Number(toDisplayLength(e.value_cm)) })), {
                  format: (v) => `${v} ${lengthUnit()}`,
                  goal: goal != null ? Number(toDisplayLength(goal)) : null,
                })
              : null,
            h("div", { style: { marginTop: "8px" } },
              ...[...entries].reverse().map((e) =>
                h("div", { class: "row-between", style: { padding: "6px 0", alignItems: "center", borderTop: "1px solid var(--border)" } },
                  h("div", null,
                    h("span", { style: { fontWeight: 600, fontSize: ".86rem" } }, `${toDisplayLength(e.value_cm)} ${lengthUnit()}`),
                    h("span", { class: "dim", style: { fontSize: ".76rem", marginLeft: "8px" } }, fmtDate(e.logged_at))
                  ),
                  h("button", { class: "icon-btn ghost danger", "aria-label": "Delete entry", onClick: () => removeEntry(e) }, icon("trash", { size: 14 }))
                )
              )
            )
          )
        : null
    );
  }

  // Value closest to `days` ago for a site's ascending entries
  function around(entries, days) {
    const target = Date.now() - days * 86400000;
    let pick = null;
    for (const e of entries) {
      const t = parseDate(e.logged_at)?.getTime() ?? 0;
      if (t <= target) pick = e;
    }
    return (pick || entries[0]).value_cm;
  }

  // Per-site goal row + progress
  function goalRow(siteKey, curVal, goal, startVal) {
    if (goal == null) {
      return h("button", {
        class: "btn btn-ghost btn-sm",
        style: { padding: "4px 8px", fontSize: ".78rem", justifyContent: "flex-start" },
        onClick: () => openGoalModal(siteKey),
      }, icon("trophy", { size: 13 }), "Set target");
    }

    const reached = curVal != null && Math.abs(curVal - goal) < 0.5;
    let detail = null;
    let frac = null;

    if (curVal == null) {
      detail = `Target: ${toDisplayLength(goal)} ${lengthUnit()}`;
    } else if (reached) {
      detail = "Target reached 🎉";
      frac = 1;
    } else {
      const remain = Number(toDisplayLength(Math.abs(curVal - goal))) || 0;
      detail = `${remain} ${lengthUnit()} to ${curVal > goal ? "lose" : "gain"} → ${toDisplayLength(goal)} ${lengthUnit()}`;
      if (startVal != null) {
        const total = goal - startVal;
        const done = curVal - startVal;
        frac = total === 0 ? 1 : Math.max(0, Math.min(1, done / total));
      }
    }

    return h("div", { class: "ms-goal", onClick: () => openGoalModal(siteKey) },
      h("div", { class: "row-between", style: { alignItems: "center" } },
        h("div", { class: "hstack", style: { gap: "6px", alignItems: "center", fontSize: ".78rem", fontWeight: 600 } },
          h("span", null, "🎯"), h("span", null, detail)),
        icon("edit", { size: 13, className: "icon muted" })
      ),
      frac != null
        ? h("div", { class: "ms-bar" },
            h("div", {
              class: "ms-bar-fill",
              style: { width: `${Math.round(frac * 100)}%`, background: reached ? "var(--success)" : "var(--accent)" },
            }))
        : null
    );
  }

  // ── Tappable Silhouette (Body Map View) ──────────────────────────────────────
  function silhouetteCard() {
    const cur = currentMap();
    const body = h("div", { class: "ms-body" });
    const holder = h("div", { class: "ms-svg-holder" });
    body.appendChild(holder);
    loadSilhouette(getBodyMap()).then((svg) => { holder.innerHTML = svg; });

    for (const mk of MARKERS) {
      const val = cur[mk.site];
      const goal = data.goals?.[mk.site];
      const reached = val != null && goal != null && Math.abs(val - goal) < 0.5;
      const state = val == null ? "empty" : reached ? "done" : "set";
      const label = val == null
        ? h("span", { class: "ms-label" }, siteLabel(mk.site), h("span", { class: "ms-plus" }, "+"))
        : h("span", { class: "ms-label" }, siteLabel(mk.site), " ", h("b", null, `${toDisplayLength(val)}`));

      body.appendChild(
        h("div", {
          class: `ms-marker ${mk.side} ${state}`,
          style: { left: `${mk.x}%`, top: `${mk.y}%` },
          role: "button",
          "aria-label": `Log ${siteLabel(mk.site)}`,
          onClick: () => openLogModal(mk.site),
        }, h("span", { class: "ms-dot" }), label)
      );
    }

    return h("div", { class: "card ms-figure-wrap" },
      h("div", { style: { textAlign: "center", marginBottom: "8px" } },
        h("span", { class: "dim", style: { fontSize: ".8rem" } }, "Tap any marker to log a measurement.")
      ),
      body
    );
  }

  // ── Logging Modal with Sex-Aware Landmark Guidance ──────────────────────────
  function openLogModal(presetSite) {
    let site = presetSite || "waist";
    const profileSex = profile()?.gender || getBodyMap();
    let guideSex = profileSex; // allows toggling male/female instructions inside modal

    const siteDataMap = {};
    for (const s of data.sites || []) siteDataMap[s.site] = s;

    // Elements
    const siteSel = h("select", {
      class: "select",
      style: { width: "100%" },
      onchange: (e) => {
        site = e.target.value;
        updateModalGuidance();
      },
    }, ...SITES.map(([k, label]) => h("option", { value: k, selected: k === site }, label)));

    const input = h("input", {
      class: "input",
      type: "number",
      inputmode: "decimal",
      step: "0.1",
      placeholder: `Value in ${lengthUnit()}`,
      style: { flex: "1" },
    });

    const tipBox = h("div", { class: "ms-tip-box" });
    const prevRow = h("div", { class: "ms-modal-prev" });

    function updateModalGuidance() {
      const meta = SITE_META[site] || {};
      const sData = siteDataMap[site];
      const cur = sData?.current_cm;
      const goal = data.goals?.[site];

      // Previous value row
      setChildren(prevRow,
        cur != null
          ? h("span", null, `Last: `, h("b", null, `${toDisplayLength(cur)} ${lengthUnit()}`),
              sData?.entries?.length ? ` (${fmtDate(sData.entries[sData.entries.length - 1].logged_at)})` : "")
          : h("span", { class: "dim" }, "No previous logs for this site."),
        goal != null
          ? h("span", null, `Target: `, h("b", null, `${toDisplayLength(goal)} ${lengthUnit()}`))
          : null
      );

      // Pre-fill input with last logged value if empty
      if (!input.value && cur != null) {
        input.value = toDisplayLength(cur);
      }

      // Guidance text
      if (site === "waist") {
        const isF = guideSex === "female";
        setChildren(tipBox,
          h("div", { class: "ms-tip-head" },
            h("span", null, isF ? "👩 Women's Standard: Natural Waist" : "👨 Men's Standard: Navel Level"),
            h("button", {
              class: "btn btn-ghost btn-sm",
              style: { padding: "2px 6px", fontSize: ".72rem" },
              onClick: () => {
                guideSex = isF ? "male" : "female";
                updateModalGuidance();
              },
            }, `Switch to ${isF ? "Men's" : "Women's"}`)
          ),
          h("div", { class: "ms-tip-desc" }, isF ? meta.womenLandmark : meta.menLandmark),
          h("div", { class: "dim", style: { fontSize: ".72rem", marginTop: "4px" } }, meta.rationale)
        );
      } else {
        setChildren(tipBox,
          h("div", { class: "ms-tip-head" },
            h("span", null, `📍 ${meta.label} Landmark:`)
          ),
          h("div", { class: "ms-tip-desc" }, meta.landmark || ""),
          meta.rationale ? h("div", { class: "dim", style: { fontSize: ".72rem", marginTop: "4px" } }, meta.rationale) : null
        );
      }
    }

    // Steppers: +/- 0.5 cm or +/- 0.2 in
    const stepVal = lengthUnit() === "in" ? 0.2 : 0.5;
    const nudge = (dir) => {
      const cur = parseFloat(input.value) || 0;
      const next = Math.max(0, Math.round((cur + dir * stepVal) * 10) / 10);
      input.value = next;
    };

    updateModalGuidance();

    const save = async () => {
      const cm = toStorageCm(input.value);
      if (cm == null || cm <= 0) {
        toast("Enter a valid measurement", "warn");
        return;
      }
      m.close();
      try {
        await api.logMeasurement({ site, value_cm: cm });
        toast(`${siteLabel(site)} logged`, "success");
        await load();
      } catch (e) {
        toast(e.message, "error");
      }
    };

    const m = openModal({
      title: `Log Measurement`,
      content: h("div", null,
        h("div", { class: "field" }, h("label", null, "Body part"), siteSel),
        prevRow,
        tipBox,
        h("div", { class: "field" },
          h("label", null, `Measurement (${lengthUnit()})`),
          h("div", { class: "ms-stepper" },
            h("button", { class: "ms-stepper-btn", onClick: () => nudge(-1), "aria-label": `Minus ${stepVal}` }, `−${stepVal}`),
            input,
            h("button", { class: "ms-stepper-btn", onClick: () => nudge(1), "aria-label": `Plus ${stepVal}` }, `+${stepVal}`)
          )
        ),
        h("button", { class: "btn btn-primary btn-block mt", onClick: save }, "Save Measurement")
      ),
    });

    setTimeout(() => {
      input.focus();
      input.select();
    }, 60);
  }

  // ── Target Setting Modal ────────────────────────────────────────────────────
  function openGoalModal(site) {
    const existing = data.goals?.[site];
    const input = h("input", {
      class: "input",
      type: "number",
      inputmode: "decimal",
      step: "0.1",
      value: existing != null ? toDisplayLength(existing) : "",
      placeholder: `Target in ${lengthUnit()}`,
    });

    const save = async (clear) => {
      let cm = null;
      if (!clear) {
        cm = toStorageCm(input.value);
        if (cm == null || cm <= 0) {
          toast("Enter a valid target", "warn");
          return;
        }
      }
      m.close();
      try {
        await api.setMeasurementGoal({ site, target_cm: clear ? null : cm });
        toast(clear ? "Target cleared" : "Target saved", "success");
        await load();
      } catch (e) {
        toast(e.message, "error");
      }
    };

    const m = openModal({
      title: `${siteLabel(site)} Target`,
      content: h("div", null,
        h("div", { class: "field" },
          h("label", null, `Target ${siteLabel(site).toLowerCase()} (${lengthUnit()})`),
          input
        ),
        h("div", { class: "btn-row mt" },
          existing != null ? h("button", { class: "btn btn-ghost", onClick: () => save(true) }, "Clear") : null,
          h("button", { class: "btn btn-primary grow", onClick: () => save(false) }, "Save")
        )
      ),
    });

    setTimeout(() => input.focus(), 50);
  }

  async function removeEntry(e) {
    const ok = await confirmDialog({
      title: "Delete entry?",
      message: `Remove the ${toDisplayLength(e.value_cm)} ${lengthUnit()} entry from ${fmtDate(e.logged_at)}?`,
      confirmText: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteMeasurement(e.id);
      await load();
    } catch (err) {
      toast(err.message, "error");
    }
  }

  return root;
}